import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    Divider,
    IconButton,
    InputAdornment,
    Pagination,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import {Link, useNavigate} from 'react-router-dom';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {getUser} from '../helpers/crypto';
import {Constants} from '../components/Constants';

interface Order {
    id: number;
    date_time: string;
    user_id: number;
    order_sum: number;
    post_date: string | null;
    ttn_id: string | null;
    description: string | null;
    name: string;
    submited: string;
    last_name: string;
    email: string;
    phone: string;
}

interface OrderDetail {
    id: number;
    item_id: number;
    price: number;
    kol: number;
    submited: number;
    name: string;
    shortName: string;
    imgPath1: string | null;
}

interface EditState {
    post_date: string;
    ttn_id: string;
    description: string;
}

const ifSubmitedColor = (submited: string): string => {
    return submited==='1' ? '#0077CC' : '#e57373';
}

const AdminOrdersPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [orders, setOrders]   = useState<Order[]>([]);
    const [page, setPage]       = useState(1);
    const [pages, setPages]     = useState(1);
    const [total, setTotal]     = useState(0);
    const [loading, setLoading] = useState(true);
    const [dateFrom, setDateFrom]   = useState('');
    const [dateTo, setDateTo]       = useState('');
    const [lastName, setLastName]   = useState('');
    const [lastNameInput, setLastNameInput] = useState('');
    const [editId, setEditId]   = useState<number | null>(null);
    const [editData, setEditData] = useState<EditState>({ post_date: '', ttn_id: '', description: '' });
    const [saving, setSaving]   = useState(false);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [details, setDetails] = useState<Record<number, OrderDetail[]>>({});
    const [loadingDetails, setLoadingDetails] = useState(false);
    // submited overrides per order: { [orderId]: { [itemId]: 0|1 } }
    const [submitedMap, setSubmitedMap] = useState<Record<number, Record<number, number>>>({});
    const [confirming, setConfirming]   = useState<number | null>(null);
    const [alert, setAlert]     = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

    useEffect(() => {
        const u = getUser();
        if (!u) { navigate('/login'); return; }
        if (u.status !== 'ADMIN') { navigate('/'); return; }
    }, [navigate]);

    const fetchOrders = useCallback(async (p: number, df: string, dt: string, ln: string) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: String(p) });
            if (df) params.set('date_from', df);
            if (dt) params.set('date_to', dt);
            if (ln.trim()) params.set('last_name', ln.trim());
            const res  = await fetch(`${Constants.SERVER_URL}${Constants.GET_ORDERS_PREFIX}?${params}`);
            const data = await res.json();
            if (data.success) {
                setOrders(data.orders);
                setPages(data.pages);
                setTotal(data.total);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void fetchOrders(page, dateFrom, dateTo, lastName); }, [page, dateFrom, dateTo, lastName, fetchOrders]);

    const toggleDetails = async (orderId: number) => {
        if (expandedId === orderId) {
            setExpandedId(null);
            return;
        }
        setExpandedId(orderId);
        if (details[orderId]) return;
        setLoadingDetails(true);
        try {
            const res  = await fetch(`${Constants.SERVER_URL}${Constants.GET_ORDER_DETAILS_PREFIX}?id=${orderId}`);
            const data = await res.json();
            if (data.success) {
                setDetails(prev => ({ ...prev, [orderId]: data.details }));
            }
        } finally {
            setLoadingDetails(false);
        }
    };

    const startEdit = (order: Order) => {
        setEditId(order.id);
        setEditData({
            post_date:   order.post_date   || '',
            ttn_id:      order.ttn_id      || '',
            description: order.description || '',
        });
        setAlert(null);
    };

    const cancelEdit = () => setEditId(null);

    const saveEdit = async (orderId: number) => {
        setSaving(true);
        setAlert(null);
        try {
            const res  = await fetch(`${Constants.SERVER_URL}${Constants.UPDATE_ORDER_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: orderId, ...editData }),
            });
            const data = await res.json();
            if (data.success) {
                setOrders(prev => prev.map(o => o.id === orderId
                    ? { ...o, post_date: editData.post_date || null, ttn_id: editData.ttn_id || null, description: editData.description || null }
                    : o
                ));
                setEditId(null);
                setAlert({ type: 'success', msg: 'Замовлення збережено' });
                setTimeout(() => setAlert(null), 3000);
            } else {
                setAlert({ type: 'error', msg: data.error || 'Помилка збереження' });
            }
        } catch {
            setAlert({ type: 'error', msg: 'Помилка з\'єднання' });
        } finally {
            setSaving(false);
        }
    };

    const toggleSubmited = (orderId: number, itemId: number) => {
        setSubmitedMap(prev => {
            const orderMap = prev[orderId] ?? {};
            const cur = itemId in orderMap
                ? orderMap[itemId]
                : (details[orderId]?.find(d => d.item_id === itemId)?.submited ?? 0);
            return { ...prev, [orderId]: { ...orderMap, [itemId]: cur ? 0 : 1 } };
        });
    };

    const getSubmited = (orderId: number, itemId: number): number => {
        const override = submitedMap[orderId]?.[itemId];
        if (override !== undefined) return override;
        return details[orderId]?.find(d => d.item_id === itemId)?.submited ?? 0;
    };

    const confirmOrder = async (orderId: number) => {
        const orderDetails = details[orderId];
        if (!orderDetails) return;
        setConfirming(orderId);
        setAlert(null);
        try {
            const items = orderDetails.map(d => ({
                item_id:  d.item_id,
                submited: getSubmited(orderId, d.item_id),
            }));
            const res  = await fetch(`${Constants.SERVER_URL}${Constants.CONFIRM_ORDER_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order_id: orderId, items }),
            });
            const data = await res.json();
            if (data.success) {
                // Update local details with new submited values
                setDetails(prev => ({
                    ...prev,
                    [orderId]: prev[orderId].map(d => ({
                        ...d,
                        submited: getSubmited(orderId, d.item_id),
                    })),
                }));
                void fetchOrders(page, dateFrom, dateTo, lastName);
                setSubmitedMap(prev => { const n = { ...prev }; delete n[orderId]; return n; });
                const emailMsg = data.email ? ' Лист надіслано клієнту.' : ' (надсилання листа не вдалось)';
                setAlert({ type: 'success', msg: `Замовлення №${orderId} підтверджено.${emailMsg}` });
                setTimeout(() => setAlert(null), 5000);
            } else {
                setAlert({ type: 'error', msg: data.error || 'Помилка підтвердження' });
            }
        } catch {
            setAlert({ type: 'error', msg: 'Помилка з\'єднання' });
        } finally {
            setConfirming(null);
        }
    };

    const formatDate = (dt: string | null) => {
        if (!dt) return '—';
        return dt.replace('T', ' ').substring(0, 16);
    };

    return (
        <Box sx={{ bgcolor: '#f7f9fc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #e0e6ed', position: 'sticky', top: 0, zIndex: 100 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', height: 70, gap: 2 }}>
                        <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
                            <LocalHospitalIcon sx={{ color: '#0077CC', fontSize: 28 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#1a1a2e' }}>{Constants.APP_LABEL}</Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                            sx={{ textTransform: 'none', color: '#555' }}
                            onClick={() => navigate('/catalog')}>← Каталог</Button>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <ReceiptOutlinedIcon sx={{ color: '#0077CC', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Замовлення</Typography>
                    <Chip label={`${total} всього`} size="small" sx={{ ml: 1 }} />
                </Box>

                {/* Filters */}
                <Box
                    component="form"
                    onSubmit={e => { e.preventDefault(); setPage(1); setLastName(lastNameInput); }}
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5, alignItems: 'center' }}
                >
                    <TextField
                        label="Від" type="date" size="small"
                        value={dateFrom}
                        onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        label="До" type="date" size="small"
                        value={dateTo}
                        onChange={e => { setDateTo(e.target.value); setPage(1); }}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        placeholder="Прізвище клієнта..." size="small"
                        value={lastNameInput}
                        onChange={e => setLastNameInput(e.target.value)}
                        sx={{ width: 220 }}
                        slotProps={{
                            input: {
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                                endAdornment: lastNameInput ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => { setLastNameInput(''); setLastName(''); setPage(1); }}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }
                        }}
                    />
                    <Button type="submit" variant="outlined" size="small" sx={{ textTransform: 'none', borderRadius: 2 }}>
                        Знайти
                    </Button>
                    {(dateFrom || dateTo || lastName) && (
                        <Button size="small" sx={{ textTransform: 'none', color: '#888' }}
                            onClick={() => { setDateFrom(''); setDateTo(''); setLastName(''); setLastNameInput(''); setPage(1); }}>
                            Скинути
                        </Button>
                    )}
                </Box>

                {alert && (
                    <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
                        {alert.msg}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : orders.length === 0 ? (
                    <Paper elevation={0} sx={{ border: '1px solid #e0e6ed', borderRadius: 3, p: 6, textAlign: 'center', color: '#aaa' }}>
                        <Typography>Замовлень ще немає</Typography>
                    </Paper>
                ) : (
                    <Paper elevation={0} sx={{ border: '1px solid #e0e6ed', borderRadius: 3, overflow: 'hidden' }}>
                        {/* Table header — desktop only */}
                        {!isMobile && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '60px 140px 1fr 100px 110px 130px 1fr 100px', gap: 1.5, px: 2, py: 1.5, bgcolor: '#f0f4f8', borderBottom: '1px solid #e0e6ed' }}>
                                {['№', 'Дата', 'Клієнт', 'Сума', 'Відправка', 'ТТН', 'Примітки', ''].map((h, i) => (
                                    <Typography key={i} sx={{ fontWeight: 600, fontSize: 12, color: '#555' }}>{h}</Typography>
                                ))}
                            </Box>
                        )}

                        {orders.map((order, idx) => (
                            <Box key={order.id}>
                                {editId === order.id ? (
                                    /* ── Edit row ── */
                                    <Box sx={{ px: { xs: 2, sm: 2 }, py: 2, bgcolor: '#f8fbff', borderLeft: '3px solid #0077CC' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                            <Chip label={`№ ${order.id}`} size="small" color="primary" />
                                            <Typography sx={{ fontWeight: 600, fontSize: 14
                                                , color: ifSubmitedColor(order.submited) }}>
                                                {order.name} {order.last_name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 13, color: '#888' }}>{formatDate(order.date_time)}</Typography>
                                            <Typography sx={{ fontWeight: 700, color: ifSubmitedColor(order.submited), fontSize: 14 }}>
                                                {order.order_sum.toFixed(2)} ₴
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '160px 1fr 1fr' },
                                            gap: 1.5 }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5 }}>Дата відправки</Typography>
                                                <TextField size="small" fullWidth type="date" value={editData.post_date}
                                                    onChange={e => setEditData(p => ({ ...p, post_date: e.target.value }))}
                                                    slotProps={{ inputLabel: { shrink: true } }} />
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5 }}>Номер декларації (ТТН)</Typography>
                                                <TextField size="small" fullWidth value={editData.ttn_id}
                                                    onChange={e => setEditData(p => ({ ...p, ttn_id: e.target.value }))}
                                                    placeholder="20450000000000" />
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5 }}>Примітки</Typography>
                                                <TextField size="small" fullWidth value={editData.description}
                                                    onChange={e => setEditData(p => ({ ...p, description: e.target.value }))} />
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                                            <Button size="small" variant="outlined" startIcon={<CloseOutlinedIcon />}
                                                onClick={cancelEdit} sx={{ textTransform: 'none', borderRadius: 2 }}>
                                                Скасувати
                                            </Button>
                                            <Button size="small" variant="contained" startIcon={saving ? undefined : <SaveOutlinedIcon />}
                                                disabled={saving} onClick={() => saveEdit(order.id)}
                                                sx={{ textTransform: 'none', borderRadius: 2 }}>
                                                {saving ? <CircularProgress size={16} color="inherit" /> : 'Зберегти'}
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    /* ── View row ── */
                                    <Box>
                                        {isMobile ? (
                                            /* Mobile card */
                                            <Box sx={{ px: 2, py: 1.5 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                                            <Chip label={`#${order.id}`} size="small" color="primary" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                                                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: ifSubmitedColor(order.submited) }}>
                                                                {order.order_sum.toFixed(2)} ₴
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{ fontWeight: 600, fontSize: 14
                                                            , color: ifSubmitedColor(order.submited) }}>
                                                            {order.name} {order.last_name}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 12, color: '#888' }}>{formatDate(order.date_time)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <IconButton size="small" onClick={() => toggleDetails(order.id)} sx={{ color: '#0077CC' }}>
                                                            {expandedId === order.id ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => startEdit(order)} sx={{ color: '#666' }}>
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                                {(order.post_date || order.ttn_id || order.description) && (
                                                    <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {order.post_date && (
                                                            <Typography sx={{ fontSize: 12, color: '#2e7d32' }}>📅 {order.post_date}</Typography>
                                                        )}
                                                        {order.ttn_id && (
                                                            <Typography sx={{ fontSize: 12, color: '#444' }}>ТТН: {order.ttn_id}</Typography>
                                                        )}
                                                        {order.description && (
                                                            <Typography sx={{ fontSize: 12, color: '#666' }}>{order.description}</Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        ) : (
                                            /* Desktop row */
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '60px 140px 1fr 100px 110px 130px 1fr 100px', gap: 1.5, px: 2, py: 1.5, alignItems: 'center' }}>
                                                <Chip label={`#${order.id}`} size="small" color="primary" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                                                <Typography sx={{ fontSize: 12, color: '#666' }}>{formatDate(order.date_time)}</Typography>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 600, fontSize: 13
                                                        , color: ifSubmitedColor(order.submited) }}>
                                                        {order.name} {order.last_name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 11, color: '#999' }}>{order.email}</Typography>
                                                </Box>
                                                <Typography sx={{ fontWeight: 700, fontSize: 13, color: ifSubmitedColor(order.submited) }}>
                                                    {order.order_sum.toFixed(2)} ₴
                                                </Typography>
                                                <Typography sx={{ fontSize: 12, color: order.post_date ? '#2e7d32' : '#bbb' }}>
                                                    {order.post_date || '—'}
                                                </Typography>
                                                <Typography sx={{ fontSize: 12, color: order.ttn_id ? '#444' : '#bbb',
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {order.ttn_id || '—'}
                                                </Typography>
                                                <Typography sx={{ fontSize: 12, color: '#555',
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {order.description || '—'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                    <Tooltip title={expandedId === order.id ? 'Згорнути' : 'Деталі замовлення'}>
                                                        <IconButton size="small" onClick={() => toggleDetails(order.id)} sx={{ color: '#0077CC' }}>
                                                            {expandedId === order.id ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Редагувати">
                                                        <IconButton size="small" onClick={() => startEdit(order)} sx={{ color: '#666' }}>
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        )}
                                        {/* ── Order details (collapsed) ── */}
                                        <Collapse in={expandedId === order.id}>
                                            <Box sx={{ px: 2, pb: 2, bgcolor: '#fafcff', borderTop: '1px solid #e8f0f8' }}>
                                                {loadingDetails && !details[order.id] ? (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                                        <CircularProgress size={24} />
                                                    </Box>
                                                ) : (details[order.id] || []).length === 0 ? (
                                                    <Typography sx={{ color: '#aaa', fontSize: 13, py: 1.5 }}>Деталі відсутні</Typography>
                                                ) : (
                                                    <Box sx={{ mt: 1.5 }}>
                                                        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: '#555' }}>
                                                            Наявність товарів — відмітьте що є в наявності:
                                                        </Typography>
                                                        {(details[order.id] || []).map((d, di) => {
                                                            const isAvail = !!getSubmited(order.id, d.item_id);
                                                            return (
                                                            <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, py: 1,
                                                                borderBottom: di < details[order.id].length - 1 ? '1px solid #eef2f7' : 'none',
                                                                bgcolor: isAvail ? 'rgba(46,125,50,0.04)' : 'rgba(198,40,40,0.03)',
                                                                borderRadius: 1, px: 0.5 }}>
                                                                <Checkbox
                                                                    checked={isAvail}
                                                                    onChange={() => toggleSubmited(order.id, d.item_id)}
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{ p: 0.5, flexShrink: 0 }}
                                                                />
                                                                {d.imgPath1 ? (
                                                                    <Box component="img" src={d.imgPath1} alt={d.name}
                                                                        sx={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 1, bgcolor: '#f0f4f8', p: 0.5, flexShrink: 0 }} />
                                                                ) : (
                                                                    <Box sx={{ width: 44, height: 44, borderRadius: 1, bgcolor: '#f0f4f8', flexShrink: 0 }} />
                                                                )}
                                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                    <Typography sx={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                        color: isAvail ? '#2e7d32' : '#c62828' }}>
                                                                        {d.name}
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: 11, color: '#999' }}>{d.shortName}</Typography>
                                                                </Box>
                                                                <Typography sx={{ fontSize: 13, color: '#555', flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
                                                                    {d.kol} шт. × {d.price.toFixed(2)} ₴
                                                                </Typography>
                                                                <Chip
                                                                    label={isAvail ? 'Є' : 'Нема'}
                                                                    size="small"
                                                                    color={isAvail ? 'success' : 'error'}
                                                                    variant="outlined"
                                                                    sx={{ fontSize: 11, height: 22, flexShrink: 0 }}
                                                                />
                                                            </Box>
                                                            );
                                                        })}
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                                                            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0077CC' }}>
                                                                Разом: {order.order_sum.toFixed(2)} ₴
                                                            </Typography>
                                                            <Button
                                                                variant="contained"
                                                                color="success"
                                                                size="small"
                                                                startIcon={confirming === order.id ? undefined : <TaskAltIcon />}
                                                                disabled={confirming === order.id}
                                                                onClick={() => confirmOrder(order.id)}
                                                                sx={{ textTransform: 'none', borderRadius: 2 }}
                                                            >
                                                                {confirming === order.id
                                                                    ? <CircularProgress size={16} color="inherit" />
                                                                    : 'Підтвердити та надіслати лист'}
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Box>
                                )}
                                {idx < orders.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </Paper>
                )}

                {pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                            count={pages} page={page}
                            onChange={(_, v) => { setPage(v); setEditId(null); setExpandedId(null); }}
                            color="primary" shape="rounded"
                        />
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default AdminOrdersPage;
